import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    companyApiKeysApi,
    integrationKeys,
    platformApiKeysApi,
    type ApiKeysApi,
    type NewApiKeyInput,
} from '@/entities/integration';

/** Van wie de sleutels zijn: het eigen bedrijf, of het platform (de beheer-MCP). */
export type ApiKeyOwner = 'company' | 'platform';

const APIS: Record<ApiKeyOwner, ApiKeysApi> = {
    company: companyApiKeysApi,
    platform: platformApiKeysApi,
};

export function useApiKeys(owner: ApiKeyOwner) {
    return useQuery({
        queryKey: integrationKeys.list(owner),
        queryFn: () => APIS[owner].list(),
    });
}

export function useCreateApiKey(owner: ApiKeyOwner) {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: NewApiKeyInput) => APIS[owner].create(input),
        onSuccess: () => client.invalidateQueries({ queryKey: integrationKeys.list(owner) }),
    });
}

export function useRevokeApiKey(owner: ApiKeyOwner) {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (uuid: string) => APIS[owner].revoke(uuid),
        onSuccess: () => client.invalidateQueries({ queryKey: integrationKeys.list(owner) }),
    });
}

/** Wat een sleutel deed; pas opgehaald als iemand het openklapt. */
export function useApiKeyActivity(owner: ApiKeyOwner, uuid: string, enabled: boolean) {
    return useQuery({
        queryKey: integrationKeys.activity(owner, uuid),
        queryFn: () => APIS[owner].activity(uuid),
        enabled,
    });
}
